import React from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth, Role } from '../auth/auth.context'
import { LoadingErrorState } from '../components/loading-error-state'
import ModuleDetail from '../features/modules/detail.page'
import ModuleFormPage from '../features/modules/module-form.page'
import WeiterbildungFormPage from '../features/weiterbildung/weiterbildung-form.page'
import AdminDashboard from '../features/admin/admin-dashboard.page'
import CompaniesOverview from '../features/companies/companies-overview.page'

// Wrapper component to handle authentication loading and redirects
export const AuthWrapper = ({ children, allow }: { children: React.ReactNode; allow?: Role[] }) => {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  // Show loading state using LoadingErrorState component
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingErrorState isLoading={true} loadingMessage='Authentifizierung...' />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to='/login' replace state={{ from: location }} />
  }

  // Check role permissions
  if (allow && !allow.includes(user.role)) {
    return <Navigate to='/' replace />
  }

  return <>{children}</>
}

// Component to route users to their role-specific dashboard
export const RoleBasedDashboard = () => {
  const { user } = useAuth()
  console.log('user.role', user?.role)
  if (user?.role === 'SUPERADMIN') {
    return <CompaniesOverview />
  } else if (user?.role === 'ADMIN') {
    return <AdminDashboard />
  } else if (user?.role === 'EMPLOYEE') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-surface-secondary'>
        <div className='max-w-md w-full mx-4'>
          <div className='bg-white rounded-2xl shadow-sm border border-primary-100 p-8 text-center'>
            <div className='w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-primary-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z' />
              </svg>
            </div>
            <h1 className='text-2xl font-bold text-neutral-900 mb-2'>Mobile App Required</h1>
            <p className='text-neutral-600 mb-6'>
              As an employee, please use the Start Smart mobile app to access your onboarding, training, and time tracking features.
            </p>
            <p className='text-sm text-neutral-500'>
              Download the app from your device's app store or contact your administrator for assistance.
            </p>
          </div>
        </div>
      </div>
    )
  } else {
    return null
  }
}

// Detail view for a module. If `:id` captures "new", render the create form.
export const RoleBasedModuleView = () => {
  const { user } = useAuth()
  const { id } = useParams()
  const location = useLocation()
  if (user?.role === 'EMPLOYEE') {
    return null
  }
  if (id === 'new') {
    return location.pathname.startsWith('/weiterbildung') ? (
      <WeiterbildungFormPage />
    ) : (
      <ModuleFormPage />
    )
  }
  return <ModuleDetail />
}
